import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useStoreContext } from './StoreContext';

interface TrackingContextType {
  trackEvent: (eventName: string, params?: Record<string, any>) => void;
  trackAddToCart: (product: any, quantity: number) => void;
  trackViewContent: (product: any) => void;
  trackInitiateCheckout: (cartItems: any[], totalPaise: number) => void;
  trackPurchase: (order: any) => void;
  trackSearch: (query: string) => void;
  consentGiven: boolean | null;
  giveConsent: () => void;
  declineConsent: () => void;
  getBrowserTrackingPayload: () => Record<string, string>;
}

const TrackingContext = createContext<TrackingContextType | null>(null);

// ─── SCRIPT LOADER ────────────────────────────────────────────────────────────
function loadScript(src: string, id: string): Promise<void> {
  return new Promise((resolve) => {
    if (document.getElementById(id)) { resolve(); return; }
    const s  = document.createElement('script');
    s.id     = id;
    s.src    = src;
    s.async  = true;
    s.onload = () => resolve();
    document.head.appendChild(s);
  });
}

// ─── GA CLIENT ID READER ──────────────────────────────────────────────────────
function getGAClientId(): string {
  try {
    const match = document.cookie.match(/_ga=GA\d+\.\d+\.(\d+\.\d+)/);
    return match ? match[1] : '';
  } catch { return ''; }
}

// ─── COOKIE HELPERS ───────────────────────────────────────────────────────────
function getFBCookies() {
  const fbp = document.cookie.match(/_fbp=(fb\.\d+\.\d+\.\d+)/)?.[1] || '';
  const fbc = document.cookie.match(/_fbc=(fb\.\d+\.\d+\.\d+)/)?.[1] || '';
  return { fbp, fbc };
}

// ─── PROVIDER ────────────────────────────────────────────────────────────────
export function TrackingProvider({ children }: { children: ReactNode }) {
  const { settings } = useStoreContext();
  const location            = useLocation();
  const tracking            = (settings as any)?.tracking || {};

  const [scriptsLoaded,  setScriptsLoaded]  = useState(false);
  const [consentGiven,   setConsentGiven]   = useState<boolean | null>(() => {
    try {
      const stored = localStorage.getItem('dilzee_tracking_consent');
      if (stored === 'true')  return true;
      if (stored === 'false') return false;
      return null; // not yet decided
    } catch { return null; }
  });

  const cookieConsentEnabled = tracking.cookie_consent_enabled === true;
  // If consent is required: only track when consent is explicitly given
  // If consent not required: track always
  const canTrack = !cookieConsentEnabled || consentGiven === true;

  // ── Load tracking scripts ─────────────────────────────────────────────────
  useEffect(() => {
    if (!canTrack) return;
    if (scriptsLoaded) return;

    const loads: Promise<void>[] = [];

    // Meta Pixel
    if (tracking.meta_pixel_id) {
      (function(f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
        if (f.fbq) return;
        n = f.fbq = function() {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
        t = b.createElement(e); t.async = !0;
        t.src = v; s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

      (window as any).fbq('init', tracking.meta_pixel_id);
    }

    // GA4
    if (tracking.ga4_measurement_id) {
      loads.push(loadScript(
        `https://www.googletagmanager.com/gtag/js?id=${tracking.ga4_measurement_id}`,
        'ga4-script'
      ).then(() => {
        (window as any).dataLayer = (window as any).dataLayer || [];
        function gtag(...args: any[]) { (window as any).dataLayer.push(args); }
        (window as any).gtag = gtag;
        gtag('js', new Date());
        gtag('config', tracking.ga4_measurement_id, { send_page_view: false });

        // Google Ads
        if (tracking.google_ads_conversion_id) {
          gtag('config', tracking.google_ads_conversion_id);
        }
      }));
    }

    // TikTok Pixel
    if (tracking.tiktok_pixel_id) {
      (function(w: any, d: any, t: any) {
        w.TiktokAnalyticsObject = t;
        const ttq = w[t] = w[t] || [];
        ttq.methods = ['page','track','identify','instances','debug','on','off','once','ready','alias','group','enableCookie','disableCookie'];
        ttq.setAndDefer = function(obj: any, method: any) {
          obj[method] = function() { obj.push([method].concat(Array.prototype.slice.call(arguments, 0))); };
        };
        ttq.methods.forEach((method: string) => ttq.setAndDefer(ttq, method));
        ttq.instance = function(t: string) { const i = ttq._i[t] || []; return i; };
        ttq.load = function(k: string) {
          const s = d.createElement('script'); s.type = 'text/javascript'; s.async = true;
          s.src = 'https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=' + k;
          const a = d.getElementsByTagName('script')[0]; a.parentNode.insertBefore(s, a);
          ttq._i = ttq._i || {}; ttq._i[k] = [];
        };
      })(window, document, 'ttq');
      (window as any).ttq.load(tracking.tiktok_pixel_id);
    }

    Promise.all(loads).then(() => setScriptsLoaded(true));
  }, [canTrack, tracking.meta_pixel_id, tracking.ga4_measurement_id, tracking.tiktok_pixel_id]);

  // ── PageView on route change ──────────────────────────────────────────────
  useEffect(() => {
    if (!canTrack || !scriptsLoaded) return;

    const url = window.location.href;

    // Meta
    if ((window as any).fbq) (window as any).fbq('track', 'PageView');

    // GA4
    if ((window as any).gtag && tracking.ga4_measurement_id) {
      (window as any).gtag('event', 'page_view', {
        page_location: url,
        page_title:    document.title,
      });
    }

    // TikTok
    if ((window as any).ttq) (window as any).ttq.page();

  }, [location.pathname, canTrack, scriptsLoaded]);

  // ── Generic event tracker ─────────────────────────────────────────────────
  const trackEvent = useCallback((eventName: string, params: Record<string, any> = {}) => {
    if (!canTrack) return;
    if ((window as any).fbq) (window as any).fbq('track', eventName, params);
    if ((window as any).gtag) (window as any).gtag('event', eventName, params);
  }, [canTrack]);

  // ── ViewContent (product page) ────────────────────────────────────────────
  const trackViewContent = useCallback((product: any) => {
    if (!canTrack) return;
    const pricePaise = product.price || 0;

    if ((window as any).fbq) {
      (window as any).fbq('track', 'ViewContent', {
        content_ids:  [product.id],
        content_name: product.name,
        content_type: 'product',
        value:        pricePaise / 100,
        currency:     'INR',
      });
    }
    if ((window as any).gtag) {
      (window as any).gtag('event', 'view_item', {
        currency: 'INR',
        value:    pricePaise / 100,
        items: [{
          item_id:       product.id,
          item_name:     product.name,
          item_category: product.category || '',
          price:         pricePaise / 100,
        }],
      });
    }
    if ((window as any).ttq) {
      (window as any).ttq.track('ViewContent', {
        content_id:   product.id,
        content_name: product.name,
        content_type: 'product',
        value:        pricePaise / 100,
        currency:     'INR',
      });
    }
  }, [canTrack]);

  // ── AddToCart ─────────────────────────────────────────────────────────────
  const trackAddToCart = useCallback((product: any, quantity: number) => {
    if (!canTrack) return;
    const pricePaise = product.price || 0;
    const total      = pricePaise * quantity;

    if ((window as any).fbq) {
      (window as any).fbq('track', 'AddToCart', {
        content_ids:  [product.id],
        content_name: product.name,
        content_type: 'product',
        value:        total / 100,
        currency:     'INR',
        num_items:    quantity,
      });
    }
    if ((window as any).gtag) {
      (window as any).gtag('event', 'add_to_cart', {
        currency: 'INR',
        value:    total / 100,
        items: [{
          item_id:   product.id,
          item_name: product.name,
          price:     pricePaise / 100,
          quantity,
        }],
      });
    }
    if ((window as any).ttq) {
      (window as any).ttq.track('AddToCart', {
        content_id:   product.id,
        content_name: product.name,
        value:        total / 100,
        currency:     'INR',
        quantity,
      });
    }
  }, [canTrack]);

  // ── InitiateCheckout ──────────────────────────────────────────────────────
  const trackInitiateCheckout = useCallback((cartItems: any[], totalPaise: number) => {
    if (!canTrack) return;
    const contentIds = cartItems.map(i => i.product_id || i.id).filter(Boolean);
    const numItems   = cartItems.reduce((s, i) => s + (i.quantity || 1), 0);

    if ((window as any).fbq) {
      (window as any).fbq('track', 'InitiateCheckout', {
        content_ids:  contentIds,
        content_type: 'product',
        num_items:    numItems,
        value:        totalPaise / 100,
        currency:     'INR',
      });
    }
    if ((window as any).gtag) {
      (window as any).gtag('event', 'begin_checkout', {
        currency: 'INR',
        value:    totalPaise / 100,
        items:    cartItems.map(i => ({
          item_id:   i.product_id || i.id,
          item_name: i.name,
          price:     (i.price || 0) / 100,
          quantity:  i.quantity || 1,
        })),
      });
    }
    if ((window as any).ttq) {
      (window as any).ttq.track('InitiateCheckout', {
        value:    totalPaise / 100,
        currency: 'INR',
        content_id: contentIds,
      });
    }
  }, [canTrack]);

  // ── Purchase (browser-side — fires on order confirmation page) ────────────
  // Note: server-side CAPI fires from the Worker simultaneously
  // event_id = orderId ensures Meta deduplicates and only counts once
  const trackPurchase = useCallback((order: any) => {
    if (!canTrack) return;
    let orderItems: any[] = [];
    try { orderItems = typeof order.items === 'string' ? JSON.parse(order.items) : order.items || []; } catch {}

    const contentIds = orderItems.map((i: any) => i.product_id || i.id).filter(Boolean);
    const numItems   = orderItems.reduce((s: number, i: any) => s + (i.quantity || 1), 0);
    const totalRupees = (order.total || 0) / 100;

    // Meta Pixel Purchase — event_id matches CAPI event_id for deduplication
    if ((window as any).fbq) {
      (window as any).fbq('track', 'Purchase', {
        content_ids:  contentIds,
        content_type: 'product',
        value:        totalRupees,
        currency:     'INR',
        num_items:    numItems,
        order_id:     order.id,
      }, { eventID: order.id }); // ← deduplication key
    }

    // GA4 purchase
    if ((window as any).gtag) {
      (window as any).gtag('event', 'purchase', {
        transaction_id: order.id,
        value:          totalRupees,
        currency:       'INR',
        items:          orderItems.map((i: any) => ({
          item_id:   i.product_id || i.id,
          item_name: i.name,
          price:     (i.price || 0) / 100,
          quantity:  i.quantity || 1,
        })),
      });

      // Google Ads conversion
      if (tracking.google_ads_conversion_id && tracking.google_ads_conversion_label) {
        (window as any).gtag('event', 'conversion', {
          send_to:         `${tracking.google_ads_conversion_id}/${tracking.google_ads_conversion_label}`,
          value:           totalRupees,
          currency:        'INR',
          transaction_id:  order.id,
        });
      }
    }

    // TikTok PlaceAnOrder
    if ((window as any).ttq) {
      (window as any).ttq.track('PlaceAnOrder', {
        content_id:  contentIds,
        value:       totalRupees,
        currency:    'INR',
        quantity:    numItems,
        order_id:    order.id,
      });
    }
  }, [canTrack, tracking.google_ads_conversion_id, tracking.google_ads_conversion_label]);

  // ── Search ────────────────────────────────────────────────────────────────
  const trackSearch = useCallback((query: string) => {
    if (!canTrack || !query) return;
    if ((window as any).fbq) (window as any).fbq('track', 'Search', { search_string: query });
    if ((window as any).gtag) (window as any).gtag('event', 'search', { search_term: query });
    if ((window as any).ttq) (window as any).ttq.track('Search', { query });
  }, [canTrack]);

  // ── Browser payload for server-side enrichment ────────────────────────────
  // Storefront passes these in the order request body so the Worker can use them
  const getBrowserTrackingPayload = useCallback((): Record<string, string> => {
    const { fbp, fbc } = getFBCookies();
    return {
      tracking_fbp:           fbp,
      tracking_fbc:           fbc,
      tracking_ga_client_id:  getGAClientId(),
      tracking_source_url:    window.location.href,
    };
  }, []);

  // ── Consent handlers ──────────────────────────────────────────────────────
  const giveConsent = useCallback(() => {
    setConsentGiven(true);
    try { localStorage.setItem('dilzee_tracking_consent', 'true'); } catch {}
  }, []);

  const declineConsent = useCallback(() => {
    setConsentGiven(false);
    try { localStorage.setItem('dilzee_tracking_consent', 'false'); } catch {}
  }, []);

  return (
    <TrackingContext.Provider value={{
      trackEvent, trackAddToCart, trackViewContent,
      trackInitiateCheckout, trackPurchase, trackSearch,
      consentGiven, giveConsent, declineConsent,
      getBrowserTrackingPayload,
    }}>
      {children}

      {/* ── COOKIE CONSENT BANNER ────────────────────────────────────────── */}
      {cookieConsentEnabled && consentGiven === null && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10000,
          background: '#1a1a2e', color: 'white',
          padding: '16px 24px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.3)',
        }}>
          <p style={{ fontSize: 13, margin: 0, maxWidth: 700, lineHeight: 1.5, color: 'rgba(255,255,255,0.85)' }}>
            🍪 {tracking.cookie_consent_text || 'We use cookies to personalise your experience and improve our services.'}
          </p>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={declineConsent}
              style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              Decline
            </button>
            <button onClick={giveConsent}
              style={{ padding: '8px 16px', background: '#fd3c01', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
              Accept All
            </button>
          </div>
        </div>
      )}
    </TrackingContext.Provider>
  );
}

export function useTracking() {
  const ctx = useContext(TrackingContext);
  if (!ctx) throw new Error('useTracking must be used inside TrackingProvider');
  return ctx;
}
